/**
 * Pure logic behind the Box folders "Sync progress" column.
 *
 * Bender's enumeration completion timestamp only says *discovery* finished and batches were
 * queued. Qualification — the stage that actually writes rows into the asset index — runs
 * asynchronously afterwards and can lag by seconds, minutes, or 30-90 minutes on a redrive
 * tail. This module normalizes Bender's two deploy-order-compatible wire shapes, then turns
 * the run-scoped counters into one derived state so JSX never reasons about absent fields.
 *
 * Kept free of React and Meridian so the derivation table is simple to unit test. See
 * docs/system-specs/modules/box-folders.md (Sync progress) for the reasoning behind each
 * state, and Bender's docs/system-specs/modules/sync-target.md (Qualification Progress)
 * for the write side.
 */

import { DateTime } from 'luxon';
import {
  type EnumerationDto,
  type SyncTargetDto,
  SyncTargetStatuses,
} from 'src/types/sync-target.types';

export type BoxFolderProgressKind =
  | 'NEVER_SCANNED'
  | 'UNREPORTED'
  | 'SCANNING'
  | 'NO_ASSETS'
  | 'SYNCING'
  | 'SYNCED';

export type BoxFolderProgress =
  /** No scan has ever begun. Every freshly enrolled folder. */
  | { kind: 'NEVER_SCANNED' }
  /** A scan completed, but before these counters shipped — there is genuinely no progress data. */
  | { kind: 'UNREPORTED' }
  /** A scan is in flight, or the last one failed before completing. No denominator exists. */
  | { kind: 'SCANNING' }
  /** The scan completed and enqueued nothing: an empty folder, or everything excluded. */
  | { kind: 'NO_ASSETS'; fileCount?: number }
  | { kind: 'SYNCED'; fileCount?: number }
  | {
    kind: 'SYNCING';
    /** Clamped to `total`, so an over-shot counter never reads past complete. */
    qualified: number;
    total: number;
    /** Held inside `[1, 99]` while genuinely in progress — see `toPercent`. */
    percent: number;
    fileCount?: number;
    lastQualifiedAt?: string;
  };

/**
 * Converts either Bender wire contract into the nested shape used by this UI.
 *
 * Fallback is deliberately per object, never per field. Once `enumeration` exists, a non-null
 * object is the authoritative snapshot. A malformed nested value such as `null` normalizes to an
 * empty snapshot rather than falling back, because stale flat fields must never be mixed into a
 * nested response or erase the distinctions between never scanned, unreported, and scanning.
 */
export function normalizeEnumeration (syncTarget: SyncTargetDto): EnumerationDto {
  if (syncTarget.enumeration !== undefined) {
    return typeof syncTarget.enumeration === 'object' && syncTarget.enumeration !== null
      ? syncTarget.enumeration
      : {};
  }

  return {
    completedAt: syncTarget.lastEnumerationAt,
    fileCount: syncTarget.lastEnumerationFileCount,
    unitCount: syncTarget.lastEnumerationUnitCount,
    qualifiedUnitCount: syncTarget.qualifiedUnitCount,
    lastQualifiedAt: syncTarget.lastQualifiedUnitAt,
  };
}

/**
 * A counter is only trusted when it is a non-negative integer. Bender writes these with
 * `ADD` and plain `Number` schema fields, and they cross an untyped JSON boundary, so a
 * negative, fractional, `NaN`, or infinite value is treated as absent rather than rendered.
 */
function usable (value: number | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Percentage for an in-progress run, deliberately not a plain round.
 *
 * `Math.round(399 / 400 * 100)` is `100`, which would print "100% complete" beside
 * "Syncing assets"; `Math.floor(1 / 400 * 100)` is `0`, which would claim nothing had
 * finished when a batch demonstrably had. So the result is held inside `[1, 99]` whenever a
 * batch has finished but not all of them have, and the number never contradicts its label.
 *
 * Only ever called with `0 <= qualified < total` and `total >= 1`; the caller returns
 * `SYNCED` before reaching here, so there is deliberately no 100% case to handle.
 */
function toPercent (qualified: number, total: number): number {
  if (qualified <= 0) return 0;
  return Math.min(99, Math.max(1, Math.floor((qualified / total) * 100)));
}

/**
 * `fileCount` is omitted when zero so the cell's rule stays uniform ("render the file line
 * iff `fileCount !== undefined`") rather than a per-state condition. Zero only ever occurs
 * alongside `NO_ASSETS`, whose label already says it.
 */
function toFileCount (fileCount: number | undefined): number | undefined {
  return usable(fileCount) && fileCount > 0 ? fileCount : undefined;
}

/**
 * Derives what the Sync progress column should show for one enrolled folder.
 *
 * The branches are ordered and the first match wins. Two of them turn on the *absence* of a
 * field, which is load-bearing in both directions:
 *
 * - `start()` writes `qualifiedUnitCount: 0` unconditionally and Bender's schema declares no
 *   default, so an absent numerator proves no scan ever began. That is the only way to tell a
 *   never-scanned folder from a row written before these counters shipped.
 * - `start()` removes the file/batch counts, so an absent `unitCount` means a run is in flight
 *   (or failed before completing), never zero. `completedAt` is not cleared and may still hold
 *   the previous run's completion, so it only distinguishes the absent-numerator cases.
 *
 * `status` is deliberately not read. Progress accrues in any status and continues after
 * `status` has returned to `ACTIVE`, so the two are orthogonal; a failed run surfaces through
 * the `ERROR` tag in the Status column, which is what keeps this column from duplicating it.
 */
export function deriveBoxFolderProgress (syncTarget: SyncTargetDto): BoxFolderProgress {
  const {
    completedAt,
    fileCount,
    unitCount,
    qualifiedUnitCount,
    lastQualifiedAt,
  } = normalizeEnumeration(syncTarget);

  if (!usable(qualifiedUnitCount)) {
    return completedAt ? { kind: 'UNREPORTED' } : { kind: 'NEVER_SCANNED' };
  }

  if (!usable(unitCount)) {
    // A numerator may already have accrued here — Bender lets batches be consumed before the
    // scan stamps its denominator — but a numerator without a denominator is not progress.
    return { kind: 'SCANNING' };
  }

  const renderedFileCount = toFileCount(fileCount);

  if (unitCount === 0) {
    return { kind: 'NO_ASSETS', fileCount: renderedFileCount };
  }

  const total = unitCount;
  const qualified = Math.min(qualifiedUnitCount, total);

  if (qualified >= total) {
    return { kind: 'SYNCED', fileCount: renderedFileCount };
  }

  return {
    kind: 'SYNCING',
    qualified,
    total,
    percent: toPercent(qualified, total),
    fileCount: renderedFileCount,
    lastQualifiedAt,
  };
}

/**
 * Whether this folder's sync is still expected to move, i.e. whether the page should keep
 * asking for it.
 *
 * `status` is read here and only here. That is not folding progress into status: it decides
 * whether to keep *polling*, not what to *render*. Both status checks are load-bearing:
 *
 * - `ERROR`/`PAUSED` stop the polling. `fail()` clears neither counter and `complete()` never
 *   resets the numerator, so a run that died after `start()` reads as `SCANNING` forever and
 *   would otherwise be polled until the tab closed.
 * - `QUEUED`/`IN_PROGRESS` start it, regardless of what the counters derive to. `queue()`
 *   stamps the status without touching the counters, so between "Sync now" and the enumerator
 *   calling `start()` the counters still describe the *previous* run — typically reading as
 *   complete. Deriving from them alone would leave the page showing a finished sync beside a
 *   queued folder, with nothing scheduled to correct it.
 */
export function isBoxFolderSyncMoving (syncTarget: SyncTargetDto): boolean {
  const { status } = syncTarget;
  if (status === SyncTargetStatuses.ERROR || status === SyncTargetStatuses.PAUSED) {
    return false;
  }
  if (status === SyncTargetStatuses.QUEUED || status === SyncTargetStatuses.IN_PROGRESS) {
    return true;
  }

  const { kind } = deriveBoxFolderProgress(syncTarget);
  return kind === 'SYNCING' || kind === 'SCANNING';
}

/**
 * Relative time for the "last update" line, e.g. "12 minutes ago".
 *
 * Luxon's `toRelative`, not the `toRelativeCalendar` used by the row's date cells: this line
 * reports minutes-to-hours of worker activity, a scale at which day-granular output ("today")
 * says nothing. Returns `undefined` for an absent or unparseable timestamp so the caller can
 * omit the line entirely rather than render a fallback string.
 */
export function relativeFromNow (iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const dateTime = DateTime.fromISO(iso, { zone: 'utc' });
  return dateTime.isValid ? (dateTime.toRelative() ?? undefined) : undefined;
}
